import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { HERO_IMAGE } from "../lib/constants";

export const Hero = () => {
  const ref = useRef(null);
  const base = typeof window !== "undefined" && window.__introDone ? 0.2 : 2.45;
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, -70]);
  const rotateX = useTransform(scrollYProgress, [0, 1], [0, 3]);

  return (
    <section ref={ref} id="accueil" className="relative flex min-h-screen items-center overflow-hidden bg-ivory pb-24 pt-28 sm:pt-32">
      <div
        aria-hidden
        className="pointer-events-none absolute -left-6 bottom-[-7vw] select-none font-serif text-[26vw] leading-none text-espresso/[0.045]"
      >
        Vāsam
      </div>
      <div
        aria-hidden
        className="pointer-events-none absolute -right-[15%] -top-[20%] h-[70vw] w-[70vw] rounded-full opacity-70"
        style={{ background: "radial-gradient(circle, rgba(192,160,98,0.20) 0%, rgba(192,160,98,0) 62%)" }}
      />

      <div className="wrap relative grid items-center gap-16 lg:grid-cols-12 lg:gap-8">
        <div className="lg:col-span-7">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: base }} className="flex items-center gap-4">
            <span className="h-px w-12 bg-champagne" />
            <p className="eyebrow text-taupe">Maison de fragrances — pour elle &amp; pour lui</p>
          </motion.div>

          <h1 className="mt-7 font-serif text-[15vw] leading-[0.94] text-espresso sm:text-7xl lg:text-[5.4rem] xl:text-[6.2rem]">
            <span className="block overflow-hidden pb-1">
              <motion.span
                className="block"
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1.1, delay: base + 0.1, ease: [0.22, 1, 0.36, 1] }}
              >
                La signature
              </motion.span>
            </span>
            <span className="block overflow-hidden pb-2">
              <motion.span
                className="block"
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1.1, delay: base + 0.24, ease: [0.22, 1, 0.36, 1] }}
              >
                de votre <em className="italic text-champagne">présence</em>
              </motion.span>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: base + 0.5 }}
            className="mt-7 max-w-md text-[15px] leading-relaxed text-espresso/70"
          >
            Une sélection raffinée de parfums d'exception, pensée pour marquer les esprits. Le luxe du sillage, à prix
            accessible.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: base + 0.65 }}
            className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-5"
          >
            <Link data-testid="hero-cta-collection" to="/parfums" className="btn-pill bg-espresso px-9 py-4 text-ivory hover:gap-5 hover:bg-noir">
              Découvrir la collection
            </Link>
            <button
              data-testid="hero-cta-maison"
              onClick={() => window.__lenis?.scrollTo("#maison", { offset: -70 })}
              className="group flex items-center gap-3 text-[11px] uppercase tracking-[0.28em] text-espresso/80 transition-colors duration-300 hover:text-champagne"
            >
              L'univers Vāsam
              <span className="h-px w-8 bg-current transition-all duration-500 group-hover:w-12" />
            </button>
          </motion.div>
        </div>

        <div className="relative lg:col-span-5">
          <motion.div style={{ y, rotateX, transformPerspective: 1200 }} className="relative mx-auto w-[min(78vw,340px)] max-w-[440px] sm:w-[min(60vw,420px)] lg:w-full">
            <motion.div
              initial={{ opacity: 0, y: 40, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 1.2, delay: base + 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="relative"
            >
              <div className="overflow-hidden rounded-t-[999px] rounded-b-3xl shadow-[0_60px_120px_-40px_rgba(36,26,19,0.45)]">
                <img src={HERO_IMAGE} alt="Flacon de parfum Vāsam" className="aspect-[3/4] w-full object-cover" />
              </div>
              <div aria-hidden className="pointer-events-none absolute inset-3 rounded-t-[999px] rounded-b-2xl border border-ivory/40" />

              <div className="absolute -left-8 bottom-14 hidden sm:-left-12 sm:block">
                <motion.div
                  initial={{ opacity: 0, scale: 0.6 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 1, delay: base + 0.9 }}
                  className="relative flex h-28 w-28 items-center justify-center"
                >
                  <svg viewBox="0 0 100 100" className="absolute inset-0 animate-spin-slow text-espresso/70">
                    <defs>
                      <path id="seal-circle" d="M50,50 m-40,0 a40,40 0 1,1 80,0 a40,40 0 1,1 -80,0" />
                    </defs>
                    <text fill="currentColor" style={{ fontSize: "7.4px", letterSpacing: "2.4px", fontFamily: "Jost, sans-serif" }}>
                      <textPath href="#seal-circle">VĀSAM · MAISON DE FRAGRANCES · VĀSAM · MAISON DE FRAGRANCES ·</textPath>
                    </text>
                  </svg>
                  <span className="font-serif text-3xl text-champagne">V</span>
                </motion.div>
              </div>
            </motion.div>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: base + 1.05 }}
              className="mt-6 flex items-center gap-3 text-[10px] uppercase tracking-[0.3em] text-taupe"
            >
              <span className="h-px w-8 bg-taupe/50" />
              Extraits de parfum — 30 % de concentration
            </motion.p>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: base + 1.3, duration: 1 }}
        className="absolute bottom-8 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-3 md:flex"
      >
        <span className="text-[9px] uppercase tracking-[0.4em] text-espresso/50">Défiler</span>
        <motion.span
          animate={{ opacity: [0.3, 1, 0.3] }}
          transition={{ repeat: Infinity, duration: 2.4 }}
          className="h-12 w-px bg-gradient-to-b from-champagne to-transparent"
        />
      </motion.div>
    </section>
  );
};
